import * as React from 'react';
import {useDispatch} from 'react-redux';

import {convertToArray, safelyGetFilterByKey} from 'nutshell-core/filter';

import {DashboardCard} from 'shells/dashboard/card/dashboard-card';
import {Illustration} from 'shells/illustrations/illustration';
import {PromoStateOverlay} from 'shells/promo-state-overlay/promo-state-overlay';

import {OpenableModals, useOpenableModal} from '../../../openable-modals';
import type {ReportURLParams} from '../../../reports/report-types';

import * as actions from '../../analytics/report/analytics-actions';
import {AnalyticsReportChart} from '../../analytics/report/analytics-report-chart';
import {useGetAnalyticsStatus} from '../../analytics/use-get-analytics-status';

import {DEFAULT_TIMESPAN} from '../dashboard';
import {useGetWebsiteTrafficDashboard} from '../graphql/hooks';

import styles from './website-traffic-card.module.css';

type Props = {
    onParamChange: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
};

export function WebsiteTrafficCard(props: Props) {
    const activeFilters = convertToArray(props.urlParams.filter);
    const compareTo = convertToArray(props.urlParams.report);
    const dateRangeFilter = safelyGetFilterByKey(activeFilters, 'period');
    const compareToFilter = safelyGetFilterByKey(compareTo, 'compareTo');
    const {onOpen: onOpenAnalyticsInfoModal} = useOpenableModal(
        OpenableModals.ANALYTICS_ONBOARDING_MODAL
    );
    const {onOpen: onOpenAnalyticsEmbedModal} = useOpenableModal(
        OpenableModals.EMBED_ANALYTICS_MODAL
    );

    // If analytics status is not configured, we show the promo state
    const {analyticsStatus} = useGetAnalyticsStatus();
    const status = analyticsStatus ? analyticsStatus.status : undefined;

    // hardcoded since we're not using the url to feed this query
    const reportParams = {
        dateRangeType: 'period',
        facet: 'count',
        gap: 'd',
        segment: 'interactionType',
    } as const;

    const dispatch = useDispatch();
    const {websiteTrafficDashboard, isLoading} = useGetWebsiteTrafficDashboard({
        timespan: dateRangeFilter || DEFAULT_TIMESPAN,
        compareTo: compareToFilter || undefined,
    });
    const stats = websiteTrafficDashboard ? websiteTrafficDashboard.stats : [];

    React.useEffect(() => {
        dispatch(actions.requestSiteAnalyticsReportData({reportParams, filters: activeFilters}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilters]);

    return (
        <DashboardCard
            title='Website traffic'
            subtitle='Gain insights into your website traffic'
            isLoading={false}
            mainContent={{
                // @ts-expect-error
                statsProps: {stats, isLoading},
                content: (
                    <div className={styles.chart}>
                        <AnalyticsReportChart
                            activeFilters={activeFilters}
                            height={346}
                            containerHeight={346}
                            onParamChange={props.onParamChange}
                            reportParams={reportParams}
                            isFullWidth={true}
                            hasBorder={false}
                        />
                    </div>
                ),
            }}
            promoStateOverlay={
                status === 'NEVER_CONFIGURED' ? (
                    <PromoStateOverlay
                        promoStateProps={{
                            isHorizontal: true,
                            imageComponent: (
                                <Illustration src='trial-onboarding/marketing-analytics' />
                            ),
                            title: 'Learn where leads come from with Nutshell Analytics',
                            subtitle:
                                'Track where your website visitors are coming from to help your team make the right sales and marketing decisions.',
                            primaryCta: {
                                text: 'Learn more',
                                onClick: onOpenAnalyticsInfoModal,
                            },
                            secondaryCta: {
                                text: 'Set up tracking',
                                onClick: onOpenAnalyticsEmbedModal,
                            },
                            blueImageBackground: true,
                        }}
                    />
                ) : null
            }
        />
    );
}
